<template>
  <v-skeleton-loader v-if="!dataset" class="mr-2 mt-6" type="list-item-three-line"></v-skeleton-loader>
  <v-form v-else ref="dataset_form" lazy-validation>
    <v-row>
      <v-col cols="12" md="12" :class="datasetType === 'ts' ? 'pb-0' : ''">
        <p class="ma-0 pa-0 pb-3">{{ $vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.label") }}</p>
        <v-radio-group
          :row="!this.$vuetify.breakpoint.xs"
          v-model="datasetType"
          class="ml-2"
          :disabled="isCheckingMandatoryColumns"
        >
          <v-radio v-for="type in filteredDatasetTypes" :key="type.value" :value="type.value">
            <template v-slot:label>
              <div class="d-flex align-center py-0">
                <p class="radioItemLable ma-0 pa-0">{{ type.text }}</p>
                <v-tooltip bottom max-width="450">
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="info-icon">i</span>
                  </template>
                  <span>{{ type.tooltip }}</span>
                </v-tooltip>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>

      <!-- Show UploadDatasetTs component when 'ts' is selected -->
      <template v-if="datasetType === 'ts'">
        <v-col cols="12">
          <upload-dataset-ts
            ref="datasetTs"
            :prop-name="propName"
            :showtags="showtags"
            @open-cloud-picker="$emit('open-cloud-picker')"
          />
        </v-col>
      </template>

      <template v-if="datasetType !== 'ts'">
        <v-col cols="12" md="12">
          <v-text-field
            type="text"
            v-model="dataset.datasetName"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.dataset_name')"
            :rules="[required($vuetify.lang.t('$vuetify.gpt_dataset.dataset_name'))]"
            outlined
            clearable
            ref="datasetNameField"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            type="text"
            v-model="dataset.indexColumnName"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.index_column_name')"
            :rules="[required($vuetify.lang.t('$vuetify.gpt_dataset.index_column_name'))]"
            :readonly="isUpdateMode"
            outlined
            ref="indexColumnNameField"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            type="text"
            v-model="dataset.secondaryIndex"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.secondary_index_column')"
            outlined
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            type="text"
            v-model="dataset.imageColumn"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.image_url_column')"
            outlined
          />
        </v-col>

        <v-col cols="12" md="6" class="mt-2 mt-md-3">
          <v-checkbox
            v-model="dataset.fillUpEmptyIndex"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.fill_up_index_if_empty')"
            :disabled="datasetType === 'dictionary'"
          ></v-checkbox>
        </v-col>
        <v-col cols="12" md="12">
          <v-text-field
            type="text"
            v-model="dataset.description"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.description_label_txt')"
            outlined
          />
        </v-col>
        <v-col cols="12" md="12">
          <v-combobox
            v-model="dataset.tags"
            :label="$vuetify.lang.t('$vuetify.gpt_profile.dataset_tags')"
            variant="solo"
            chips
            :clearable="datasetType !== 'dictionary'"
            outlined
            :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.chip_hint')"
            :hint="!showtags ? $vuetify.lang.t('$vuetify.gpt_profile.chip_hint') : ''"
            multiple
            :class="{ 'no-dropdown-icon': !showtags }"
            @change="handleTagInput"
          >
            <!-- Custom dropdown list -->
            <template v-slot:no-data>
              <div v-if="showtags && formattedTagsItems.length > 0" class="px-4 py-2">
                <!-- Headers -->
                <div class="d-flex mb-3 pb-2 header-row">
                  <div class="dataset-name-column text-subtitle-1 font-weight-bold">
                    {{ $vuetify.lang.t("$vuetify.gpt_dataset.datasets") }}
                  </div>
                  <div class="tags-column text-subtitle-1 font-weight-bold">
                    {{ $vuetify.lang.t("$vuetify.access_tab.tags") }}
                  </div>
                </div>

                <!-- Data rows -->
                <div
                  v-for="datasetItem in formattedTagsItems"
                  :key="datasetItem.dataset"
                  class="d-flex mb-3 pb-2 data-row"
                >
                  <div class="dataset-name-column">
                    <v-tooltip bottom v-if="isDatasetNameTruncated(datasetItem.dataset)">
                      <template v-slot:activator="{ on, attrs }">
                        <div class="text-subtitle-2 font-weight-bold dataset-name-text" v-bind="attrs" v-on="on">
                          {{ truncateDatasetName(datasetItem.dataset) }}:
                        </div>
                      </template>
                      <span class="tooltip-text">{{ (datasetItem.dataset || "").toUpperCase() }}</span>
                    </v-tooltip>

                    <div v-else class="text-subtitle-2 font-weight-bold dataset-name-text">
                      {{ truncateDatasetName(datasetItem.dataset) }}:
                    </div>
                  </div>

                  <div class="tags-column">
                    <div class="tag-badges">
                      <v-chip
                        v-for="tag in datasetItem.tags"
                        :key="`${datasetItem.dataset}-${tag}`"
                        small
                        class="mr-1 mb-1 clickable"
                        :color="tagSelected(tag) ? 'primary' : 'default'"
                        @click.stop="toggleTag(tag)"
                        clickable
                      >
                        {{ tag }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Selection display -->
            <template v-slot:selection="{ item }">
              <v-chip close @click:close="removeDatasetTags(item)">
                {{ item }}
              </v-chip>
            </template>
          </v-combobox>
        </v-col>

        <v-col cols="12" md="12">
          <v-combobox
            v-model="dataset.filterableFields"
            :items="filterableFieldsChips"
            :label="$vuetify.lang.t('$vuetify.gpt_profile.filterable_fields')"
            variant="solo"
            chips
            clearable
            outlined
            :hint="$vuetify.lang.t('$vuetify.gpt_profile.filterable_chip_hint')"
            multiple
            class="no-dropdown-icon"
          >
            <template v-slot:selection="{ attrs, item, selected }">
              <v-chip @click:close="removeFilterableFields(item)" v-bind="attrs" :model-value="selected" close closable>
                <span>{{ item }}</span
                >&nbsp;
              </v-chip>
            </template>
          </v-combobox>
        </v-col>
      </template>

      <template v-if="['regular', 'dictionary'].includes(datasetType)">
        <!-- Upload source toggle + cloud provider picker (shared with the
             documents upload modal) -->
        <UploadSourceSelector
          :source="dataset.uploadSource || 'local'"
          :cloud-sources="dataset.cloudSources || []"
          :selected-provider="dataset.cloudProvider || ''"
          @change="onUploadSourceChange"
          @open-picker="openCloudPickerFor"
          @remove-item="handleRemoveCloudItem"
          @clear-provider="handleClearCloudProvider"
        />

        <!-- Local file input -->
        <v-col v-if="dataset.uploadSource !== 'cloud'" cols="12" md="12">
          <v-file-input
            ref="fileInput"
            v-model="datasetFile"
            outlined
            :accept="
              datasetType === 'dictionary'
                ? '.csv, text/csv'
                : '.json, .csv, .xlsx, .docx, application/json, text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf, .pdf, .png, .jpg, .jpeg, image/png, image/jpeg, .ppt, .pptx, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation'
            "
            @change="handleFileChange"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.dataset_file')"
            :rules="fileInputRules"
            :error-messages="
              fileErrorMessage ? (Array.isArray(fileErrorMessage) ? fileErrorMessage : [fileErrorMessage]) : []
            "
            :error="!!fileErrorMessage"
            :hint="
              isCheckingMandatoryColumns
                ? $vuetify.lang.t('$vuetify.gpt_dataset.dataset_file_type_validating')
                : $vuetify.lang.t('$vuetify.gpt_dataset.encoding_hint')
            "
            persistent-hint
            class="dataset-file-input"
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
          >
          </v-file-input>
        </v-col>

        <!-- Cloud sync settings -->
        <template v-if="dataset.uploadSource === 'cloud'">
          <v-col cols="12" :md="dataset.cloudUpdateFrequency === 'custom' ? 6 : 12">
            <v-select
              v-model="dataset.cloudUpdateFrequency"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.label')"
              outlined
              :rules="[required($vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.label'))]"
              :items="[
                { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.once'), value: 'once' },
                { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.daily'), value: 'daily' },
                {
                  text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.weekly'),
                  value: 'weekly',
                },
                {
                  text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.monthly'),
                  value: 'monthly',
                },
                {
                  text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.yearly'),
                  value: 'yearly',
                },
                {
                  text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.custom'),
                  value: 'custom',
                },
              ]"
            />
          </v-col>
          <v-col v-if="dataset.cloudUpdateFrequency === 'custom'" cols="12" md="6">
            <v-text-field
              v-model.number="dataset.cloudSyncHours"
              type="number"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.cloud_storage.sync_hours_label')"
              :hint="$vuetify.lang.t('$vuetify.gpt_dataset.cloud_storage.sync_hours_hint')"
              persistent-hint
              placeholder="e.g. 6"
              :rules="[
                required($vuetify.lang.t('$vuetify.gpt_dataset.cloud_storage.sync_hours_label')),
                v =>
                  isBlank(v) || v >= 1 || $vuetify.lang.t('$vuetify.gpt_dataset.cloud_storage.sync_hours_min_error'),
                v =>
                  isBlank(v) ||
                  v <= 23 ||
                  $vuetify.lang.t('$vuetify.gpt_dataset.cloud_storage.sync_hours_max_error'),
                v =>
                  isBlank(v) ||
                  Number.isInteger(Number(v)) ||
                  $vuetify.lang.t('$vuetify.gpt_dataset.cloud_storage.sync_hours_integer_error'),
              ]"
              min="1"
              max="23"
              outlined
              @keypress="preventInvalidChars"
            />
          </v-col>
          <v-col v-if="hasFolderSelected" cols="12" md="6">
            <v-select
              :value="dataset.cloudExtensions"
              :items="cloudFolderExtensionOptions"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.cloud_storage.extensions_label')"
              :hint="$vuetify.lang.t('$vuetify.gpt_dataset.cloud_storage.extensions_hint')"
              persistent-hint
              chips
              small-chips
              multiple
              outlined
              @input="handleCloudExtensionsChange"
            >
              <template #selection="{ item }">
                <v-chip
                  small
                  close
                  @click:close="handleCloudExtensionsChange((dataset.cloudExtensions || []).filter(e => e !== item))"
                >
                  .{{ item }}
                </v-chip>
              </template>
            </v-select>
          </v-col>
        </template>

        <v-col cols="12" v-if="isImageOrPdfOrPpt && datasetType !== 'dictionary'">
          <v-btn color="primary" @click="openMetadataEditor" outlined class="mb-2">
            <v-icon left>mdi-file-cog</v-icon>
            {{ $vuetify.lang.t("$vuetify.gpt_dataset.configure_support") }}
          </v-btn>
        </v-col>
        <v-col
          cols="12"
          v-if="isImageFile && imagePreviewUrl && datasetType !== 'dictionary' && dataset.uploadSource !== 'cloud'"
        >
          <v-card class="mb-4" elevation="1">
            <v-card-title class="subtitle-1">
              {{ $vuetify.lang.t("$vuetify.gpt_dataset.image_preview") }}
            </v-card-title>
            <v-img :src="imagePreviewUrl" max-height="300" contain class="ma-2"></v-img>
            <v-card-actions>
              <v-btn small @click="imagePreviewUrl = null" color="secondary">
                {{ $vuetify.lang.t("$vuetify.gpt_dataset.close_preview") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="12" v-if="isImageOrPdfOrPpt && dataset.metadata && datasetType !== 'dictionary'">
          <v-textarea
            v-model="formattedMetadata"
            :label="$vuetify.lang.t('$vuetify.setup_steps.metadata_step_txt')"
            outlined
            rows="3"
            readonly
            auto-grow
            :error-messages="metadataErrorMessages"
          />
        </v-col>
      </template>

      <template v-if="datasetType === 'url'">
        <v-col cols="12">
          <v-autocomplete
            v-model="contentSource"
            :items="filteredContentSources"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.content_source.label')"
            outlined
            :readonly="isUpdateMode"
            append-icon="mdi-menu-down"
          >
            <template v-slot:append>
              <div class="append-icons">
                <v-icon>mdi-menu-down</v-icon>
                <v-tooltip right max-width="350">
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="info-icon">i</span>
                  </template>
                  <span>
                    {{ $vuetify.lang.t("$vuetify.gpt_dataset.datasetType.content_source.tooltip") }}
                  </span>
                </v-tooltip>
              </div>
            </template>
          </v-autocomplete>
        </v-col>

        <template v-if="contentSource === 'socialMedia'">
          <v-col cols="12" md="6">
            <v-select
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.social_media_type.label')"
              :placeholder="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.social_media_type.label')"
              v-model="dataset.socialMediaType"
              outlined
              required
              :rules="[required(this.$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.social_media_type.label'))]"
              :items="[{ text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.social_media_type.x'), value: 'x' }]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.country_code.label')"
              v-model="dataset.countryCode"
              outlined
              :rules="[required(this.$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.country_code.label'))]"
              :items="countryLists"
            >
              <template v-slot:no-data>
                <v-list-item v-if="isCountryLoading">
                  <v-list-item-content class="text-center">
                    <v-progress-circular indeterminate :width="3" color="primary" />
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-else>
                  <v-list-item-content>
                    {{ $vuetify.lang.t("$vuetify.noDataText") }}
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-slot:append>
                <div class="append-icons">
                  <v-icon>mdi-menu-down</v-icon>
                  <v-tooltip right max-width="350">
                    <template v-slot:activator="{ on }">
                      <span v-on="on" class="info-icon">i</span>
                    </template>
                    <span>
                      {{ $vuetify.lang.t("$vuetify.gpt_dataset.datasetType.country_code.tooltip") }}
                    </span>
                  </v-tooltip>
                </div>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              type="number"
              v-model.number="dataset.socialMediaTrendsLimit"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.social_media_trends_limit.label')"
              placeholder="e.g. 1"
              :rules="[
                required($vuetify.lang.t('$vuetify.gpt_dataset.datasetType.social_media_trends_limit.label')),
                validateSocialMediaTrendsLimit,
                integerOnly,
              ]"
              min="1"
              max="30"
              outlined
              @keypress="preventInvalidChars"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              type="number"
              v-model.number="dataset.socialMediaPostsPerTrend"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.social_media_posts_per_trend.label')"
              placeholder="e.g. 1"
              :rules="[
                required($vuetify.lang.t('$vuetify.gpt_dataset.datasetType.social_media_posts_per_trend.label')),
                validateSocialMediaPostPerTrend,
                integerOnly,
              ]"
              min="1"
              outlined
              @keypress="preventInvalidChars"
            />
          </v-col>
        </template>

        <template v-if="contentSource === 'webUrl'">
          <v-col cols="12">
            <v-combobox
              ref="urls"
              v-model="dataset.urls"
              @keydown.enter="handleEnterPress"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.urls.label')"
              chips
              clearable
              outlined
              :hint="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.urls.hint')"
              persistent-hint
              :placeholder="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.urls.placeholder')"
              multiple
              class="no-dropdown-icon"
              :loading="isReachabiltyChecking"
              :error-messages="
                !isUrlReachable
                  ? $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.url.reachability_error')
                  : urlErrorMessage
              "
              :error="!!urlErrorMessage || !isUrlReachable"
              :rules="urlValidationRules"
            >
              <template v-slot:selection="{ attrs, item, selected, index }">
                <v-chip
                  @click:close="removeUrls(index)"
                  v-bind="attrs"
                  :model-value="selected"
                  close
                  closable
                  :color="urlErrorIndexes.includes(index) ? 'error' : ''"
                  :outlined="urlErrorIndexes.includes(index)"
                  :style="urlErrorIndexes.includes(index) ? 'border: 2px solid #ff5252 !important;' : ''"
                  class="url-chip"
                  append-icon="mdi-pencil"
                >
                  <span class="thin-scroll">{{ item }} </span>
                  <v-icon right class="mr-1" small @click.stop="editUrl(index, item)">mdi-pencil</v-icon>
                </v-chip>
              </template>
            </v-combobox>
          </v-col>
        </template>
        <v-col cols="12" :md="dataset.updateFrequency === 'custom' ? 6 : 12">
          <v-select
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.label')"
            v-model="dataset.updateFrequency"
            outlined
            :rules="[required(this.$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.label'))]"
            :items="[
              { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.once'), value: 'once' },
              { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.daily'), value: 'daily' },
              { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.weekly'), value: 'weekly' },
              { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.monthly'), value: 'monthly' },
              { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.yearly'), value: 'yearly' },
              { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.update_frequency.custom'), value: 'custom' },
            ]"
          />
        </v-col>
        <v-col cols="12" md="6" v-if="dataset.updateFrequency === 'custom'">
          <v-text-field
            type="number"
            v-model.number="dataset.numOfDays"
            :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.numOfDays.label')"
            placeholder="e.g. 1"
            :rules="[
              required($vuetify.lang.t('$vuetify.gpt_dataset.datasetType.numOfDays.label')),
              validateNumOfDays,
              integerOnly,
            ]"
            min="1"
            outlined
            @keypress="preventInvalidChars"
          />
        </v-col>

        <template v-if="contentSource === 'webUrl'">
          <v-col cols="12" :md="dataset.scrapingMode === 'nested' ? 6 : 12">
            <v-select
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.scrap_mode.label')"
              v-model="dataset.scrapingMode"
              outlined
              :rules="[required(this.$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.scrap_mode.label'))]"
              :items="[
                { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.scrap_mode.absolute'), value: 'absolute' },
                { text: $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.scrap_mode.nested'), value: 'nested' },
              ]"
            />
          </v-col>

          <v-col cols="12" md="6" v-if="dataset.scrapingMode === 'nested'">
            <v-text-field
              type="number"
              v-model.number="dataset.maxDepth"
              :rules="[
                v =>
                  (!v && v !== 0) ||
                  Number(v) >= 0 ||
                  $vuetify.lang.t('$vuetify.gpt_dataset.datasetType.max_depth.min_error'),
              ]"
              min="0"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.max_depth.label')"
              :hint="$vuetify.lang.t('$vuetify.gpt_dataset.datasetType.max_depth.hint')"
              placeholder="e.g. 0"
              persistent-hint
              outlined
              @keypress="preventInvalidChars"
            />
          </v-col>
        </template>
      </template>
    </v-row>
    <metadata-editor-dialog
      v-model="metadataEditorDialog"
      :is-image-file="isImageFile"
      :is-text-extraction-file="isTextExtractionFile"
      :selected-file="dataset.datasetFile"
      :initial-metadata="dataset.metadata || {}"
      :initial-is-ocr="dataset.is_ocr"
      :initial-is-text-extraction="dataset.is_text_extraction"
      @save="handleMetadataSave"
    />
  </v-form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { UPDATE_GPT_DATASET_STATE, GET_GPT_DATASETS, GET_COUNTRY_LIST } from "@/store/_actiontypes";
import MetadataEditorDialog from "@/components/Projects/UploadDataset/MetadataEditor";
import { checkMandatoryColumns, isTextExtractableFile, removeOcrMetadata } from "@/helpers/helper";
import {
  datasetMixin,
  cloudSelectionHasImage,
  cloudSelectionHasMetadataOptions,
  cloudSelectionSupportsTextExtraction,
  cloudSelectionNeedsTextExtraction,
  deriveCloudOcrOnRemove,
  removeCloudItem,
  removeCloudProvider,
  cloudSelectionIdsEqual,
} from "@/mixins/datasetMixin";
import UploadDatasetTs from "./UploadDatasetTs.vue";
import UploadSourceSelector from "./UploadSourceSelector.vue";
import { Toast } from "@/helpers/toast";

export default {
  mixins: [datasetMixin],
  props: {
    propName: {
      type: String,
      default: "",
    },
    showtags: {
      type: Boolean,
      default: true,
    },
    isReachabiltyChecking: {
      type: Boolean,
      default: false,
    },
    isUrlReachable: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    MetadataEditorDialog,
    UploadDatasetTs,
    UploadSourceSelector,
  },
  created() {
    if (this.$route.path.includes("create-dataset") || !this.isUpdateMode) {
      this.$store.dispatch("project/SET_DATASET_TYPE", "regular");

      this.$store.commit("project/SET_GPT_SETUP_STATE", {
        dataset: {
          task_id: null,
          status: null,
          dataset_id: null,
          completed: false,
          skipped: false,
          name: "",
          description: "",
          idx_column_name: "",
          image_url_column: "",
          is_idx_fillup_if_empty: true,
          secondary_idx_column: "",
        },
      });
    } else if (this.isUpdateMode && this.dataset?.cookieExpiryDate) {
      this.prevExpiryDate = JSON.parse(JSON.stringify(this.dataset.cookieExpiryDate));
    }
  },
  computed: {
    ...mapState({
      dataset: state => state.project.selectedGptDataset,
      gptDatasets: state => state.project.gptDatasets.datasets,
      gptSetupState: state => state.project.gptSetupState,
      fileInputRules() {
        let rules = [];

        if (!this.isUpdateMode && this.dataset.uploadSource !== "cloud") {
          rules.push(
            value =>
              !!value ||
              this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_file") +
                this.$vuetify.lang.t("$vuetify.validation.required_error_msg_txt")
          );
        }

        rules.push(() => !this.fileErrorMessage || this.fileErrorMessage);

        rules.push(value => {
          if (!value) return true;
          const file = value instanceof File ? value : value[0];
          return (
            !file ||
            file.size <= this.MAX_FILE_SIZE ||
            `${this.$vuetify.lang.t("$vuetify.gpt_dataset.file_size_exceeded_error")} ${this.MAX_FILE_SIZE / 1024 / 1024}MB`
          );
        });

        return rules;
      },
      hasMetadataErrors() {
        return this.metadataErrorMessages.length > 0;
      },
    }),
    isUpdateMode() {
      return !!this.gptSetupState?.dataset?.dataset_id || !!this.$route.query.datasetId;
    },
    urlValidationRules() {
      return [
        value => {
          if (this.datasetType !== "url") return true;
          if (!value || !value.length) {
            return this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.urls.required_msg");
          }
          if (this.urlErrorIndexes.length > 0) {
            return (
              this.urlErrorMessage || this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.urls.common_error_hint")
            );
          }
          return true;
        },
      ];
    },
    isImageOrPdfOrPpt() {
      const file = this.dataset?.datasetFile;
      if (file) {
        const type = file.type || "";
        return type.includes("image") || type.includes("wordprocessingml") || isTextExtractableFile(file);
      }
      return cloudSelectionHasMetadataOptions(this.dataset?.cloudSources);
    },
    isImageFile() {
      const file = this.dataset?.datasetFile;
      if (file) return (file.type || "").includes("image");
      return cloudSelectionHasImage(this.dataset?.cloudSources);
    },
    isTextExtractionFile() {
      const file = this.dataset?.datasetFile;
      if (file) {
        return isTextExtractableFile(file);
      }
      return cloudSelectionSupportsTextExtraction(this.dataset?.cloudSources);
    },
    formattedMetadata() {
      if (!this.dataset?.metadata) return "";
      try {
        const m = JSON.parse(this.dataset?.metadata || "{}");
        return Object.keys(m).length ? JSON.stringify(m, null, 2) : "";
      } catch (e) {
        return this.dataset.metadata;
      }
    },
    reservedKeys() {
      const baseKeys = ["content"];
      return this.dataset?.indexColumnName ? [...baseKeys, this.dataset.indexColumnName] : baseKeys;
    },
    metadataErrorMessages() {
      if (!this.dataset?.metadata || !this.isImageOrPdfOrPpt) return [];

      try {
        const metadata = JSON.parse(this.dataset.metadata);
        const invalidKey = Object.keys(metadata).find(key => this.reservedKeys.includes(key));
        return invalidKey
          ? [`${this.$vuetify.lang.t("$vuetify.gpt_dataset.avoid_reserved_keys")} ${this.reservedKeys.join(", ")}`]
          : [];
      } catch (e) {
        console.error("Error parsing metadata:", e);
        return [];
      }
    },
    formattedTagsItems() {
      const items = [];

      Object.entries(this.tagsMap).forEach(([datasetName, tags]) => {
        if (!Array.isArray(tags) || tags.length === 0) return;

        const filtered = tags.filter(t => String(t).toLowerCase() !== "dictionary");

        if (filtered.length === 0) return;

        const sortedTags = [...filtered].sort((a, b) => {
          const aSelected = this.dataset.tags?.includes(a);
          const bSelected = this.dataset.tags?.includes(b);
          return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
        });

        items.push({
          dataset: datasetName,
          tags: sortedTags,
        });
      });

      return items;
    },

    datasetTypes() {
      return [
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.regular"),
          tooltip: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.regular_tooltip"),
          value: "regular",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.dictionary"),
          tooltip: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.dictionary_tooltip"),
          value: "dictionary",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.url"),
          tooltip: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.url_tooltip"),
          value: "url",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.ts"),
          tooltip: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.radio.ts_tooltip"),
          value: "ts",
        },
      ];
    },
    contentSources() {
      return [
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.content_source.webUrl"),
          value: "webUrl",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.content_source.socialMedia"),
          value: "socialMedia",
        },
      ];
    },
    filteredDatasetTypes() {
      if (this.isUpdateMode) {
        return this.datasetTypes.filter(type => type.value === this.datasetType);
      }
      return this.datasetTypes;
    },
    filteredContentSources() {
      if (this.isUpdateMode) {
        return this.contentSources.filter(source => source.value === this.contentSource);
      }
      return this.contentSources;
    },
  },
  mounted() {
    this.resetDatasetValidation();
    this.fetchTags();

    if (this.datasetType === "url" && this.contentSource === "socialMedia") {
      this.getCountryList();
    }
  },
  beforeDestroy() {
    if (!this.isUpdateMode) {
      this.$store.dispatch("project/SET_DATASET_TYPE", "regular");
      this.$store.dispatch("project/SET_CONTENT_SOURCE", "webUrl");
    }
  },
  data() {
    return {
      datasetLoader: true,
      datasetFile: null,
      tagChips: [],
      metadataEditorDialog: false,
      imagePreviewUrl: null,
      filterableFieldsChips: [],
      tagsMap: {},
      isDictionaryDataset: false,
      fileErrorMessage: null,
      isCheckingMandatoryColumns: false,
      MAX_FILE_SIZE: 100 * 1024 * 1024,
      urlEditingIndex: null,
      urlErrorIndexes: [],
      urlErrorMessage: "",
      cookiesFileError: "",
      prevExpiryDate: null,
      isCountryLoading: false,
      countryLists: [],
    };
  },
  watch: {
    dataset: {
      deep: true,
      handler() {
        if (this.datasetType !== "dictionary") {
          const index = this.dataset.tags.indexOf("dictionary");
          if (index !== -1) {
            this.dataset.tags = this.dataset.tags.filter(tag => tag !== "dictionary");
            const warnMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.tagAddWaring");
            Toast.warning(warnMsg);
          }
        }
        // this.isUrlReachable = true
        const localDataset = this.dataset;
        this.UPDATE_GPT_DATASET_STATE({ dataset: localDataset });
      },
    },
    "dataset.datasetName"(newVal, oldVal) {
      if (newVal !== oldVal && this.$refs?.datasetNameField) {
        this.$nextTick(() => {
          this.$refs?.datasetNameField.validate();
        });
      }
    },
    "dataset.indexColumnName"(newVal, oldVal) {
      if (newVal !== oldVal && this.$refs?.indexColumnNameField) {
        this.$nextTick(() => {
          this.$refs?.indexColumnNameField.validate();
        });
      }
    },
    "dataset.urls": {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.validateAllUrls();
        });
      },
    },
    "dataset.updateFrequency": {
      handler(newVal, oldVal) {
        if (oldVal === "custom" && newVal !== "custom") {
          this.dataset.numOfDays = null;
        }
      },
    },
    "dataset.cloudUpdateFrequency": {
      handler(newVal, oldVal) {
        if (oldVal === "custom" && newVal !== "custom") {
          this.dataset.cloudSyncHours = null;
        }
      },
    },
    "dataset.scrapingMode": {
      handler(newVal, oldVal) {
        if (oldVal === "nested" && newVal !== "nested") {
          this.dataset.maxDepth = null;
        }
      },
    },
    datasetType: {
      immediate: true,
      handler(newType, oldType) {
        if (!this.dataset || newType === oldType || !oldType) return;

        if (newType === "dictionary") {
          this.dataset.fillUpEmptyIndex = true;
        }
        this.contentSource = "webUrl";
        // Clear file and error message
        this.$set(this.dataset, "datasetFile", null);
        this.$set(this.dataset, "is_ocr", false);
        this.$set(this.dataset, "is_text_extraction", true);
        this.$set(this.dataset, "metadata", null);
        const keepsCloud = newType !== "url" && this.dataset.uploadSource === "cloud";
        this.$set(this.dataset, "uploadSource", keepsCloud ? "cloud" : "local");
        this.$set(this.dataset, "cloudProvider", "");
        this.$set(this.dataset, "cloudSources", []);
        this.$set(this.dataset, "cloudExtensions", []);
        if (!keepsCloud) {
          this.$set(this.dataset, "cloudUpdateFrequency", "");
          this.$set(this.dataset, "cloudSyncHours", null);
        }
        this.datasetFile = null;
        this.fileErrorMessage = null;

        // Reset file input validation
        this.$nextTick(() => {
          if (this.$refs.fileInput) {
            this.$refs.fileInput.resetValidation();
          }
        });

        this.UPDATE_GPT_DATASET_STATE({ dataset: { ...this.dataset } });
      },
    },
    contentSource: {
      immediate: true,
      handler(newSource, oldSource) {
        if (!this.dataset || newSource === oldSource || !oldSource) return;
        if (newSource === "webUrl") {
          this.dataset.scrapingMode = "absolute";
        } else if (newSource === "socialMedia") {
          this.dataset.scrapingMode = null;
          this.getCountryList();
        }

        // Reset form validation
        this.$nextTick(() => {
          if (this.$refs.dataset_form) {
            this.$refs.dataset_form.resetValidation();
          }
        });

        this.UPDATE_GPT_DATASET_STATE({ dataset: { ...this.dataset } });
      },
    },
    isImageFile: {
      immediate: true,
      handler(isImage) {
        if (this.dataset) this.dataset.is_ocr = !!isImage;
      },
    },
    // The picker lives in the parent view, so a fresh selection arrives as state.
    "dataset.cloudSources": {
      handler(sources, previous) {
        if (!sources?.length || sources === previous) return;
        if (["dictionary", "ts"].includes(this.datasetType)) return;
        const idsOf = list =>
          new Set(
            (list || []).flatMap(s => (s.items || []).map(i => `${s.provider}:${s.account_id || ""}:${i.id}`)),
          );
        const prevIds = idsOf(previous);
        const hasNewItem = (sources || []).some(s =>
          (s.items || []).some(i => !prevIds.has(`${s.provider}:${s.account_id || ""}:${i.id}`)),
        );
        if (!hasNewItem) return;
        if (cloudSelectionHasMetadataOptions(sources)) this.openMetadataEditor();
      },
    },
    gptDatasets: {
      immediate: true,
      handler(datasets) {
        if (!Array.isArray(datasets)) {
          return;
        }

        this.tagsMap = {};
        datasets.forEach((dataset, index) => {
          const key = dataset.name?.trim() || `dataset_${index + 1}`;
          this.tagsMap[key] = dataset.tags || [];
        });
      },
    },
    isCheckingMandatoryColumns(newVal) {
      this.$emit("mandatory-status", newVal);
    },
  },
  methods: {
    ...mapActions("project", [UPDATE_GPT_DATASET_STATE, GET_GPT_DATASETS, GET_COUNTRY_LIST]),


    normalizeUrl(url) {
      if (!url) return "";
      let normalized = url.trim().toLowerCase();
      // Remove all trailing slashes
      normalized = normalized.replace(/\/+$/, "");
      return normalized;
    },

    setUrlErrors(errorIndexes = [], check = "reachability") {
      // check = 'reachability' / 'duplicacy'
      this.urlErrorIndexes = errorIndexes || [];
      const invalidUrls = errorIndexes.map(index => this.dataset.urls?.[index]).filter(Boolean);

      let errorMsg = "";
      if (invalidUrls.length) {
        if (check === "duplicacy") {
          errorMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.urls.duplicate_error");
        } else if (check === "reachability") {
          errorMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.urls.reachability_error");
        }

        this.urlErrorMessage = errorMsg;
      }

      this.$nextTick(() => {
        if (this.$refs.urls) {
          this.$refs.urls.validate();
        }
      });
    },

    removeUrls(index) {
      this.dataset.urls.splice(index, 1);

      if (this.urlEditingIndex === index) {
        this.urlEditingIndex = null;
      }

      // Clear all errors and re-validate
      this.urlErrorIndexes = [];
      this.urlErrorMessage = "";

      this.$nextTick(() => {
        this.validateAllUrls();
      });
    },

    editUrl(index, url) {
      this.urlEditingIndex = index;
      this.$nextTick(() => {
        if (this.$refs.urls) {
          this.$refs.urls.lazySearch = url;
          this.$refs.urls.focus();
          const input = this.$refs.urls.$el.querySelector("input");
          if (input) {
            input.select();
          }
        }
      });
    },

    validateAllUrls() {
      if (!this.dataset.urls || this.dataset.urls.length === 0) {
        this.urlErrorIndexes = [];
        this.urlErrorMessage = "";
        return true;
      }

      const formatErrorIndexes = [];
      const invalidUrls = [];

      // Check URL format
      this.dataset.urls.forEach((url, index) => {
        const validation = this.validateUrl(url);
        if (validation !== true) {
          formatErrorIndexes.push(index);
          invalidUrls.push(url);
        }
      });

      const duplicateIndexes = this.findDuplicateUrls();
      const allErrorIndexes = [...new Set([...formatErrorIndexes, ...duplicateIndexes])];
      this.urlErrorIndexes = allErrorIndexes;
      // Build error message
      const errorMessages = [];

      if (invalidUrls.length > 0) {
        const formatError = this.$vuetify.lang.t("$vuetify.project.url_validation_error");
        errorMessages.push(formatError);
      }

      if (duplicateIndexes.length) {
        const duplicateError = this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.urls.duplicate_error");
        errorMessages.push(duplicateError);
      }

      this.urlErrorMessage = errorMessages[0] || "";

      return allErrorIndexes.length === 0;
    },

    findDuplicateUrls() {
      if (!this.dataset.urls?.length) return [];

      const seen = new Map(); // normalized URL -> array of indices
      const duplicateIndices = new Set();

      this.dataset.urls.forEach((url, index) => {
        const normalized = this.normalizeUrl(url);
        if (!normalized) return;

        if (seen.has(normalized)) {
          // Mark current index as duplicate
          duplicateIndices.add(index);
          // Mark ALL previous occurrences as duplicates too
          seen.get(normalized).forEach(i => duplicateIndices.add(i));
          seen.get(normalized).push(index);
        } else {
          seen.set(normalized, [index]);
        }
      });

      return [...duplicateIndices];
    },

    validateNumOfDays(v) {
      const num = Number(v);
      if (Number.isInteger(num) && num < 1) {
        return this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.numOfDays.min_error");
      }
      return true;
    },
    validateSocialMediaTrendsLimit(v) {
      const num = Number(v);
      if (Number.isInteger(num)) {
        if (num < 1)
          return this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.social_media_trends_limit.min_error");
        if (num > 30)
          return this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.social_media_trends_limit.max_error");
      }
      return true;
    },
    validateSocialMediaPostPerTrend(v) {
      const num = Number(v);
      if (Number.isInteger(num) && num < 1) {
        return this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.social_media_posts_per_trend.min_error");
      }
      return true;
    },
    integerOnly(v) {
      return /^[0-9]+$/.test(v) || this.$vuetify.lang.t("$vuetify.gpt_profile.integer_required");
    },
    handleEnterPress(event) {
      if (this.urlEditingIndex !== null) {
        event.preventDefault();
        event.stopPropagation();

        const newURL = this.$refs.urls?.lazySearch;

        if (!newURL || !newURL.trim()) {
          return;
        }

        const trimmedURL = newURL.trim();

        // Update the URL (even if duplicate)
        this.$set(this.dataset.urls, this.urlEditingIndex, trimmedURL);

        // Clear input and editing state
        this.$refs.urls.lazySearch = "";
        this.urlEditingIndex = null;

        // Validate all URLs - this will detect and mark duplicates with red border
        this.$nextTick(() => {
          this.validateAllUrls();
        });
      }
    },
    resetDatasetValidation() {
      this.$nextTick(() => {
        if (this.$refs.dataset_form?.resetValidation) {
          this.$refs.dataset_form.resetValidation();
        }
        if (this.$refs.fileInput?.resetValidation) {
          this.$refs.fileInput.resetValidation();
        }
        if (this.$refs.datasetTs?.resetValidation) {
          this.$refs.datasetTs.resetValidation();
        }
      });
    },

    async fetchTags() {
      try {
        await this.GET_GPT_DATASETS({
          project_id: this.$route.query.pid,
          size: 100,
        });
      } catch (error) {
        console.error("Error fetching datasets for tags:", error);
      }
    },

    tagSelected(tag) {
      return this.dataset.tags?.includes(tag) || false;
    },
    normalizeTagIfDictionary(tag) {
      if (typeof tag !== "string") return tag;
      const tagTrim = tag.trim();
      return tagTrim.toLowerCase() === "dictionary" ? "dictionary" : tag;
    },
    toggleTag(tag) {
      if (!this.dataset.tags) {
        this.dataset.tags = [];
      }

      const normalizedTag = this.normalizeTagIfDictionary(tag);

      const index = this.dataset.tags.indexOf(normalizedTag);
      if (index === -1) {
        this.dataset.tags.push(normalizedTag);
        if (!this.fileErrorMessage && this.datasetFile) {
          this.handleFileChange(this.datasetFile);
        }
      } else {
        this.dataset.tags.splice(index, 1);
      }
    },
    handleTagInput(newTags) {
      const tags = Array.isArray(newTags) ? newTags : [];

      if (tags.length === 0) {
        if (this.fileErrorMessage) {
          this.fileErrorMessage = null;
          if (this.$refs.fileInput) {
            this.$refs.fileInput.resetValidation();
          }
        }
        return;
      }

      const normalizedTags = tags.map(t => this.normalizeTagIfDictionary(t));
      this.dataset.tags = [...new Set(normalizedTags)];

      if (normalizedTags.includes("dictionary") && this.datasetFile && !this.fileErrorMessage) {
        this.handleFileChange(this.datasetFile);
      }
    },
    onDictionaryChange(val) {
      const tag = "dictionary";
      if (!this.dataset.tags) {
        this.dataset.tags = [];
      }
      const idx = this.dataset.tags.indexOf(tag);
      if (val) {
        if (idx === -1) this.dataset.tags.push(tag);
        this.dataset.fillUpEmptyIndex = true;

        if (!this.fileErrorMessage && this.datasetFile) {
          this.handleFileChange(this.datasetFile);
        }
      } else {
        if (idx !== -1) this.dataset.tags.splice(idx, 1);
        if (this.fileErrorMessage) {
          this.fileErrorMessage = null;
          if (this.$refs.fileInput) {
            this.$refs.fileInput.resetValidation();
          }
        }
      }
    },

    onUploadSourceChange(source) {
      // Re-selecting the active source must not wipe what the user configured.
      if (source === (this.dataset.uploadSource || "local")) return;
      if (this.imagePreviewUrl) {
        URL.revokeObjectURL(this.imagePreviewUrl);
        this.imagePreviewUrl = null;
      }
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          uploadSource: source,
          // Cleared together, as in resetFileState — metadata and is_ocr
          // describe the file being dropped here.
          datasetFile: null,
          is_ocr: false,
          metadata: null,
          ...(source === "cloud" && {
            cloudUpdateFrequency: this.dataset?.cloudUpdateFrequency || "once",
          }),
        },
      });
      this.datasetFile = null;
      this.fileErrorMessage = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset();
      }
    },
    handleRemoveCloudItem({ provider, itemId }) {
      const before = this.dataset.cloudSources;
      const cloudSources = removeCloudItem(before, provider, itemId);
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          cloudSources,
          cloudSourcesDirty:
            this.dataset.cloudSourcesDirty ||
            !cloudSelectionIdsEqual(before, cloudSources),
          is_ocr: deriveCloudOcrOnRemove(cloudSources, this.dataset.is_ocr),
          is_text_extraction: cloudSelectionNeedsTextExtraction(cloudSources)
            ? this.dataset.is_text_extraction
            : false,
        },
      });
    },
    handleClearCloudProvider(provider) {
      const before = this.dataset.cloudSources;
      const cloudSources = removeCloudProvider(before, provider);
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          cloudSources,
          cloudSourcesDirty:
            this.dataset.cloudSourcesDirty ||
            !cloudSelectionIdsEqual(before, cloudSources),
          is_ocr: deriveCloudOcrOnRemove(cloudSources, this.dataset.is_ocr),
          is_text_extraction: cloudSelectionNeedsTextExtraction(cloudSources)
            ? this.dataset.is_text_extraction
            : false,
        },
      });
    },
    required(fieldName) {
      return value => {
        return (
          (value !== null && value !== undefined && value !== "") ||
          `${fieldName}${this.$vuetify.lang.t("$vuetify.validation.required_error_msg_txt")}`
        );
      };
    },
    handleFileChange(file) {
      this.isCheckingMandatoryColumns = true;
      if (!file) {
        this.fileErrorMessage = null;
        this.isCheckingMandatoryColumns = false;
        this.resetFileState();
        return;
      }

      const fileObj = file instanceof File ? file : Array.isArray(file) ? file[0] : file;

      if (fileObj && fileObj.size > this.MAX_FILE_SIZE) {
        this.fileErrorMessage = `${this.$vuetify.lang.t("$vuetify.gpt_dataset.file_size_exceeded_error")} ${this.MAX_FILE_SIZE / 1024 / 1024}MB`;
        this.isCheckingMandatoryColumns = false;
        this.resetFileState();
        return;
      }

      const hasFileChanged = this.dataset?.datasetFile !== file;
      this.datasetFile = file;
      const isDictionary = this.dataset?.tags?.includes("dictionary");
      if (isDictionary) {
        this.fileErrorMessage = null;
        const isCsv =
          (file.type && file.type.includes("csv")) || (file.name && file.name.toLowerCase().endsWith(".csv"));
        if (!isCsv) {
          this.fileErrorMessage = `${this.$vuetify.lang.t("$vuetify.gpt_dataset.dictionary.only_csv_note")}`;
          this.isCheckingMandatoryColumns = false;
          return;
        }

        checkMandatoryColumns(file)
          .then(() => {
            this.UPDATE_GPT_DATASET_STATE({
              dataset: {
                ...this.dataset,
                datasetFile: file,
                is_ocr: file.type.includes("image"),
                is_text_extraction: isTextExtractableFile(file),
              },
            });
          })
          .catch(err => {
            if (Array.isArray(err)) {
              this.fileErrorMessage = err.join(", ");
            } else if (typeof err === "object" && err.message) {
              this.fileErrorMessage = err.message;
            } else {
              this.fileErrorMessage = String(err);
            }
            this.resetFileState();
          })
          .finally(() => {
            this.isCheckingMandatoryColumns = false;
          });
      } else {
        this.UPDATE_GPT_DATASET_STATE({
          dataset: {
            ...this.dataset,
            datasetFile: file,
            is_ocr: hasFileChanged ? file.type.includes("image") : this.dataset.is_ocr,
            is_text_extraction: hasFileChanged ? isTextExtractableFile(file) : this.dataset.is_text_extraction,
            metadata: hasFileChanged ? null : this.dataset.metadata,
          },
        });
        if (this.isImageFile) {
          this.imagePreviewUrl = URL.createObjectURL(file);
        }
        if (this.isImageOrPdfOrPpt) {
          this.openMetadataEditor();
        }
        this.isCheckingMandatoryColumns = false;
      }
    },
    resetFileState() {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          datasetFile: null,
          is_ocr: false,
          is_text_extraction: true,
          metadata: null,
        },
      });
    },

    onDatasetTagsChange(val) {
      const trimmed = val.map(tag => tag.trim()).filter(tag => tag !== "");
      this.dataset.tags = Array.from(new Set(trimmed));
    },
    filterableFieldsTagsChange(val) {
      const trimmed = val.map(tag => tag.trim()).filter(tag => tag !== "");
      this.dataset.filterableFields = Array.from(new Set(trimmed));
    },
    removeDatasetTags(item) {
      if (this.datasetType === "dictionary" && item === "dictionary") {
        const warnMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.tagRemoveWaring");
        Toast.warning(warnMsg);
      } else {
        this.dataset.tags.splice(this.dataset.tags.indexOf(item), 1);
      }
    },
    removeFilterableFields(item) {
      this.dataset.filterableFields.splice(this.dataset.filterableFields.indexOf(item), 1);
    },
    openMetadataEditor() {
      this.metadataEditorDialog = true;
    },
    handleMetadataSave({ metadata, isOcr, isTextExtraction }) {
      const cleanedMetadata = isOcr ? metadata : removeOcrMetadata(metadata);

      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          metadata: cleanedMetadata || {},
          is_ocr: isOcr,
          is_text_extraction: isTextExtraction,
        },
      });
    },
    truncateDatasetName(datasetName) {
      if (!datasetName) return "";
      const upperCaseName = datasetName.toUpperCase();
      const maxLength = 30; // Adjust this value based on your needs

      if (upperCaseName.length <= maxLength) {
        return upperCaseName;
      }

      return upperCaseName.substring(0, maxLength) + "...";
    },

    isDatasetNameTruncated(datasetName) {
      if (!datasetName) return false;
      return datasetName.toUpperCase().length > 30;
    },
    getCountryList() {
      this.isCountryLoading = true;
      this.countryLists = [];
      this.GET_COUNTRY_LIST({
        project_id: this.$route.query.pid,
        socialMediaType: this.dataset.socialMediaType,
      })
        .then(res => {
          const countryCodes = res?.data?.country_codes || [];
          countryCodes.map(code => {
            code = code.toString().trim().toUpperCase();
            const country = {
              text: this.getCountryName(code),
              value: code,
            };
            this.countryLists.push(country);
          });
        })
        .catch(error => {
          console.error(error);
          this.countryLists = [{ text: "Japan", value: "JP" }];
        })
        .finally(() => {
          this.isCountryLoading = false;
        });
    },
  },
};
</script>

<style scoped>
[type="url"] {
  font-size: 16px;
}
.dataset-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.tag-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.no-dropdown-icon >>> .v-input__append-inner .v-icon {
  display: none !important;
}

/* Make chips clickable */
.v-chip.clickable {
  cursor: pointer;
  transition: all 0.2s;
}

.v-chip.clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Column alignment styles */
.header-row {
  border-bottom: 1px solid #e0e0e0;
}

.data-row {
  border-bottom: 1px solid #f5f5f5;
}

.dataset-name-column {
  min-width: 200px;
  max-width: 200px;
  flex-shrink: 0;
  padding-right: 16px;
}

.tags-column {
  flex: 1;
  min-width: 0; /* Allow flex item to shrink below content size */
}

.dataset-name-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tooltip-text {
  font-size: 0.875rem;
}

.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  pointer-events: auto;
}
.radioItemLable {
  margin-bottom: 0;
}
.v-radio.v-radio--is-disabled .radioItemLable {
  color: gray;
  opacity: 0.8;
}
.v-radio.v-item--active .radioItemLable {
  color: rgba(0, 0, 0, 0.87);
}
.cookies_expiry_date >>> input[type="date"]::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}

.cookies_expiry_date >>> input[type="date"]::-moz-calendar-picker-indicator {
  display: none;
}

.thin-scroll {
  display: block;
  overflow-x: auto;
  white-space: nowrap;
  padding-left: 2px;
}

.thin-scroll::-webkit-scrollbar {
  height: 4px;
}

.thin-scroll::-webkit-scrollbar-thumb {
  background: #bbb;
  border-radius: 2px;
}

.thin-scroll::-webkit-scrollbar-track {
  background: transparent;
}
::v-deep(.v-autocomplete.v-select.v-input--is-focused .url-chip ~ input) {
  flex: 1 1 100%;
  box-sizing: border-box;
}
</style>
